import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

// Auth token management (localStorage)
const TOKEN_KEY = "oficina.access_token";
const REFRESH_KEY = "oficina.refresh_token";

export function getAccessToken() { return localStorage.getItem(TOKEN_KEY); }
export function getRefreshToken() { return localStorage.getItem(REFRESH_KEY); }
export function setTokens(access, refresh) {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// Attach Bearer token to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// On 401, try to refresh the access token once before clearing tokens.
// Multiple concurrent 401s share the same in-flight refresh promise.
let refreshPromise = null;

async function performRefresh() {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  try {
    const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refresh });
    if (data?.access_token) {
      setTokens(data.access_token, refresh);
      return data.access_token;
    }
    return null;
  } catch {
    return null;
  }
}

// Endpoints where a 401 must NOT trigger refresh (to avoid loops/wrong flows)
const NO_REFRESH_ENDPOINTS = ["/auth/login", "/auth/refresh", "/auth/google", "/auth/forgot-password", "/auth/reset-password"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config;
    const url = original?.url || "";
    const skip = NO_REFRESH_ENDPOINTS.some((p) => url.includes(p));

    // Only handle 401 on refreshable endpoints, once per request
    if (status === 401 && !skip && original && !original._retry) {
      original._retry = true;
      refreshPromise = refreshPromise || performRefresh().finally(() => { refreshPromise = null; });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      // Refresh failed — clear tokens and redirect
      clearTokens();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const login = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const logoutApi = () => api.post("/auth/logout").catch(() => {}).finally(clearTokens);
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPasswordApi = (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);
export const changePasswordApi = (current_password, new_password) => api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);
export const googleAuthApi = (session_id) => api.post("/auth/google", { session_id }).then((r) => r.data);

// Clients
export const fetchClients = () => api.get("/clients").then((r) => r.data);
export const createClient = (data) => api.post("/clients", data).then((r) => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then((r) => r.data);

// Vehicles
export const fetchVehicles = (clientId) =>
  api.get("/vehicles", { params: clientId ? { client_id: clientId } : {} }).then((r) => r.data);
export const createVehicle = (data) => api.post("/vehicles", data).then((r) => r.data);
export const updateVehicle = (id, data) => api.put(`/vehicles/${id}`, data).then((r) => r.data);
export const deleteVehicle = (id) => api.delete(`/vehicles/${id}`).then((r) => r.data);
export const lookupVehiclePlaca = (placa) =>
  api.post(`/vehicles/lookup-placa/${encodeURIComponent(placa)}`).then((r) => r.data);
export const fetchVehicleServiceOrders = (vehicleId) =>
  api.get(`/vehicles/${vehicleId}/service-orders`).then((r) => r.data);

// Parts
export const fetchParts = () => api.get("/parts").then((r) => r.data);
export const createPart = (data) => api.post("/parts", data).then((r) => r.data);
export const updatePart = (id, data) => api.put(`/parts/${id}`, data).then((r) => r.data);
export const deletePart = (id) => api.delete(`/parts/${id}`).then((r) => r.data);

// Service catalog
export const fetchServiceCatalog = () => api.get("/services-catalog").then((r) => r.data);
export const createServiceCatalog = (data) => api.post("/services-catalog", data).then((r) => r.data);
export const updateServiceCatalog = (id, data) => api.put(`/services-catalog/${id}`, data).then((r) => r.data);
export const deleteServiceCatalog = (id) => api.delete(`/services-catalog/${id}`).then((r) => r.data);

// Service Orders
export const fetchServiceOrders = (status) =>
  api.get("/service-orders", { params: status ? { status } : {} }).then((r) => r.data);
export const fetchServiceOrder = (id) => api.get(`/service-orders/${id}`).then((r) => r.data);
export const createServiceOrder = (data) => api.post("/service-orders", data).then((r) => r.data);
export const updateServiceOrder = (id, data) => api.put(`/service-orders/${id}`, data).then((r) => r.data);
export const deleteServiceOrder = (id) => api.delete(`/service-orders/${id}`).then((r) => r.data);
export const serviceOrderPdfUrl = (id) => {
  const token = getAccessToken();
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${API_BASE}/service-orders/${id}/pdf${qs}`;
};

// Downloads the OS PDF as a file (uses Bearer header, avoids token in URL logs).
export const downloadServiceOrderPdf = async (id, numero) => {
  const res = await api.get(`/service-orders/${id}/pdf`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  const num = numero != null ? String(numero).padStart(4, "0") : id;
  a.href = url;
  a.download = `OS-${num}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
};

// Financial
export const fetchFinancial = (params = {}) => api.get("/financial", { params }).then((r) => r.data);
export const createFinancial = (data) => api.post("/financial", data).then((r) => r.data);
export const updateFinancial = (id, data) => api.put(`/financial/${id}`, data).then((r) => r.data);
export const deleteFinancial = (id) => api.delete(`/financial/${id}`).then((r) => r.data);

// Fiscal (Notes)
export const fetchFiscalConfig = () => api.get("/fiscal/config").then((r) => r.data);
export const updateFiscalConfig = (data) => api.put("/fiscal/config", data).then((r) => r.data);
export const fetchEmittedNotes = () => api.get("/fiscal/notes").then((r) => r.data);
export const emitNfse = (os_id, descricao) => api.post("/fiscal/nfse/emit", { os_id, descricao }).then((r) => r.data);
export const refreshNoteStatus = (id) => api.post(`/fiscal/notes/${id}/refresh`).then((r) => r.data);
export const cancelNote = (id, justificativa) => api.delete(`/fiscal/notes/${id}`, { params: { justificativa } }).then((r) => r.data);
export const noteDownloadUrl = (id, kind) => `${API_BASE}/fiscal/notes/${id}/download/${kind}`;
export const fetchReceivedInvoices = () => api.get("/fiscal/received").then((r) => r.data);
export const importReceivedXml = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/fiscal/received/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const addReceivedToStock = (id) => api.post(`/fiscal/received/${id}/add-to-stock`).then((r) => r.data);
export const deleteReceivedInvoice = (id) => api.delete(`/fiscal/received/${id}`).then((r) => r.data);

// Company settings
export const fetchCompany = () => api.get("/company").then((r) => r.data);
export const updateCompany = (data) => api.put("/company", data).then((r) => r.data);

// Dashboard
export const fetchDashboardStats = () => api.get("/dashboard/stats").then((r) => r.data);

// Helpers
export const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export const fmtDateOnly = (iso) => {
  if (!iso) return "-";
  try {
    // For YYYY-MM-DD strings
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    }
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
};
